import { isEqual } from "lodash";

import { AdminType, StatusCode } from "api/protocols";
import {
  DeleteGroupMembershipAddon,
  DeleteGroupMembershipIncluded,
  PostGroupMembershipAddon,
  PostGroupMembershipIncluded,
  PutGroupMembershipIncluded,
} from "api/rpc/2024-04/facilityAdmin/product/groupMembership";
import { TPendingAddonMapping } from "pages/secure/facility/product/modals/AddonMappingsCard";
import { TPendingIncludedMembership } from "pages/secure/facility/product/modals/IncludedMembershipsCard";

export function includedMembershipsChanged(
  next: TPendingIncludedMembership[],
  original: TPendingIncludedMembership[],
): boolean {
  const normalize = (rows: TPendingIncludedMembership[]) =>
    [...rows]
      .map(row => ({ membership_id: Number(row.membership_id), quantity: Number(row.quantity) }))
      .sort((a, b) => a.membership_id - b.membership_id);

  return !isEqual(normalize(next), normalize(original));
}

export function addonMappingsChanged(next: TPendingAddonMapping[], original: TPendingAddonMapping[]): boolean {
  const normalize = (rows: TPendingAddonMapping[]) =>
    [...rows]
      .map(row => ({
        modifier_variant_id: Number(row.modifier_variant_id),
        membership_id: Number(row.membership_id),
      }))
      .sort((a, b) => a.modifier_variant_id - b.modifier_variant_id || a.membership_id - b.membership_id);

  return !isEqual(normalize(next), normalize(original));
}

export async function persistIncludedMemberships(
  productId: number,
  next: TPendingIncludedMembership[],
  original: TPendingIncludedMembership[],
  adminType: AdminType = AdminType.FacilityAdmin,
): Promise<boolean> {
  if (!includedMembershipsChanged(next, original)) {
    return true;
  }

  const nextByMembershipId = new Map(next.map(row => [Number(row.membership_id), row]));
  const originalByMembershipId = new Map(original.map(row => [Number(row.membership_id), row]));

  for (const row of original) {
    if (!nextByMembershipId.has(Number(row.membership_id)) && row.id) {
      const res = await DeleteGroupMembershipIncluded({ id: row.id }, true, adminType);
      if (res.status !== StatusCode.OK) {
        return false;
      }
    }
  }

  for (const row of next) {
    const previous = originalByMembershipId.get(Number(row.membership_id));
    const quantity = Number.isFinite(row.quantity) && row.quantity >= 1 ? Math.floor(row.quantity) : 1;

    if (!previous?.id) {
      const res = await PostGroupMembershipIncluded(
        { product_id: productId, membership_id: Number(row.membership_id), quantity },
        true,
        adminType,
      );
      if (res.status !== StatusCode.OK) {
        return false;
      }
    } else if (previous.quantity !== quantity) {
      const res = await PutGroupMembershipIncluded({ id: previous.id, quantity }, true, adminType);
      if (res.status !== StatusCode.OK) {
        return false;
      }
    }
  }

  return true;
}

export async function persistAddonMappings(
  productId: number,
  next: TPendingAddonMapping[],
  original: TPendingAddonMapping[],
  adminType: AdminType = AdminType.FacilityAdmin,
): Promise<boolean> {
  if (!addonMappingsChanged(next, original)) {
    return true;
  }

  const nextByVariantId = new Map(next.map(row => [Number(row.modifier_variant_id), row]));
  const originalByVariantId = new Map(original.map(row => [Number(row.modifier_variant_id), row]));

  for (const row of original) {
    if (!nextByVariantId.has(Number(row.modifier_variant_id)) && row.id) {
      const res = await DeleteGroupMembershipAddon({ id: row.id }, true, adminType);
      if (res.status !== StatusCode.OK) {
        return false;
      }
    }
  }

  for (const row of next) {
    const previous = originalByVariantId.get(Number(row.modifier_variant_id));
    if (previous?.id && previous.membership_id === row.membership_id) {
      continue;
    }

    const res = await PostGroupMembershipAddon(
      {
        product_id: productId,
        modifier_variant_id: Number(row.modifier_variant_id),
        membership_id: Number(row.membership_id),
      },
      true,
      adminType,
    );
    if (res.status !== StatusCode.OK) {
      return false;
    }
  }

  return true;
}
